import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  schedulingAPI,
  ScheduleCreateRequest,
  ScheduleUpdateRequest,
  ScheduleListFilters,
  RuleCreateRequest,
  RuleUpdateRequest,
  RuleListFilters,
  ExecutionListFilters
} from '@/services/api/scheduling';

// ============================================================================
// QUERY KEYS
// ============================================================================

export const schedulingKeys = {
  // Schedules
  schedules: ['schedules'] as const,
  schedulesList: (filters?: ScheduleListFilters) =>
    [...schedulingKeys.schedules, 'list', filters] as const,
  scheduleDetail: (id: string) => [...schedulingKeys.schedules, 'detail', id] as const,
  runningSchedules: (tenantId?: string) =>
    [...schedulingKeys.schedules, 'running', tenantId] as const,

  // Running Executions
  runningExecutions: (tenantId?: string) => ['executions', 'running', tenantId] as const,

  // Automation Rules
  rules: ['automation-rules'] as const,
  rulesList: (filters?: RuleListFilters) => [...schedulingKeys.rules, 'list', filters] as const,
  ruleDetail: (id: string) => [...schedulingKeys.rules, 'detail', id] as const,
  ruleExecutions: (ruleId: string, filters?: ExecutionListFilters) =>
    [...schedulingKeys.rules, 'executions', ruleId, filters] as const
};

// ============================================================================
// SCHEDULE HOOKS
// ============================================================================

/**
 * Hook to fetch all schedules with optional filters
 */
export function useSchedules(filters?: ScheduleListFilters) {
  return useQuery({
    queryKey: schedulingKeys.schedulesList(filters),
    queryFn: () => schedulingAPI.listSchedules(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false
  });
}

/**
 * Hook to fetch a specific schedule by ID
 */
export function useSchedule(id: string) {
  return useQuery({
    queryKey: schedulingKeys.scheduleDetail(id),
    queryFn: () => schedulingAPI.getSchedule(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000
  });
}

/**
 * Hook to create a new schedule
 */
export function useCreateSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ScheduleCreateRequest) => schedulingAPI.createSchedule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.schedules });
    }
  });
}

/**
 * Hook to update an existing schedule
 */
export function useUpdateSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ScheduleUpdateRequest }) =>
      schedulingAPI.updateSchedule(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.schedules });
      queryClient.invalidateQueries({ queryKey: schedulingKeys.scheduleDetail(id) });
    }
  });
}

/**
 * Hook to delete a schedule
 */
export function useDeleteSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => schedulingAPI.deleteSchedule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.schedules });
    }
  });
}

/**
 * Hook to manually trigger a schedule
 */
export function useTriggerSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => schedulingAPI.triggerSchedule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.schedules });
    }
  });
}

// ============================================================================
// AUTOMATION RULE HOOKS
// ============================================================================

/**
 * Hook to fetch all automation rules with optional filters
 */
export function useAutomationRules(filters?: RuleListFilters) {
  return useQuery({
    queryKey: schedulingKeys.rulesList(filters),
    queryFn: () => schedulingAPI.listRules(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false
  });
}

/**
 * Hook to fetch a specific automation rule by ID
 */
export function useAutomationRule(id: string) {
  return useQuery({
    queryKey: schedulingKeys.ruleDetail(id),
    queryFn: () => schedulingAPI.getRule(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000
  });
}

/**
 * Hook to create a new automation rule
 */
export function useCreateAutomationRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RuleCreateRequest) => schedulingAPI.createRule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.rules });
    }
  });
}

/**
 * Hook to update an existing automation rule
 */
export function useUpdateAutomationRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RuleUpdateRequest }) =>
      schedulingAPI.updateRule(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.rules });
      queryClient.invalidateQueries({ queryKey: schedulingKeys.ruleDetail(id) });
    }
  });
}

/**
 * Hook to delete an automation rule
 */
export function useDeleteAutomationRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => schedulingAPI.deleteRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.rules });
    }
  });
}

/**
 * Hook to fetch execution history for a specific rule
 */
export function useRuleExecutions(ruleId: string, filters?: ExecutionListFilters) {
  return useQuery({
    queryKey: schedulingKeys.ruleExecutions(ruleId, filters),
    queryFn: () => schedulingAPI.getRuleExecutions(ruleId, filters),
    enabled: !!ruleId,
    staleTime: 1 * 60 * 1000, // 1 minute - more frequent updates for executions
    refetchOnWindowFocus: false
  });
}

/**
 * Hook to toggle schedule enabled state
 */
export function useToggleScheduleEnabled() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, is_enabled }: { id: string; is_enabled: boolean }) =>
      schedulingAPI.updateSchedule(id, { is_enabled }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.schedules });
      queryClient.invalidateQueries({ queryKey: schedulingKeys.scheduleDetail(id) });
    }
  });
}

/**
 * Hook to toggle automation rule enabled state
 */
export function useToggleRuleEnabled() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, is_enabled }: { id: string; is_enabled: boolean }) =>
      schedulingAPI.updateRule(id, { is_enabled }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: schedulingKeys.rules });
      queryClient.invalidateQueries({ queryKey: schedulingKeys.ruleDetail(id) });
    }
  });
}

// ============================================================================
// RUNNING STATUS HOOKS
// ============================================================================

/**
 * Hook to fetch schedules with running status
 * Polls every 15 seconds when there are running schedules, otherwise every 60 seconds
 */
export function useRunningSchedules(tenantId?: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: schedulingKeys.runningSchedules(tenantId),
    queryFn: () => schedulingAPI.getRunningSchedules(tenantId),
    enabled: options?.enabled !== false,
    staleTime: 10 * 1000, // 10 seconds
    refetchInterval: query => {
      const data = query.state.data;
      // Poll every 15 seconds if there are running schedules
      if (data && data.running_count > 0) {
        return 15 * 1000;
      }
      // Otherwise poll every 60 seconds
      return 60 * 1000;
    },
    refetchOnWindowFocus: true
  });
}

/**
 * Hook to fetch currently running executions
 * Polls every 10 seconds when there are running executions
 */
export function useRunningExecutions(tenantId?: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: schedulingKeys.runningExecutions(tenantId),
    queryFn: () => schedulingAPI.getRunningExecutions(tenantId),
    enabled: options?.enabled !== false,
    staleTime: 5 * 1000, // 5 seconds
    refetchInterval: query => {
      const data = query.state.data;
      // Poll every 10 seconds if there are running executions
      if (data && data.running_count > 0) {
        return 10 * 1000;
      }
      // Otherwise poll every 30 seconds
      return 30 * 1000;
    },
    refetchOnWindowFocus: true
  });
}
